<script setup>
import { useForwardPropsEmits } from 'radix-vue'
import {
    RangeCalendarRoot,
    RangeCalendarCell,
    RangeCalendarCellTrigger,
    RangeCalendarGrid,
    RangeCalendarGridBody,
    RangeCalendarGridHead,
    RangeCalendarGridRow,
    RangeCalendarHeadCell,
    RangeCalendarHeader,
    RangeCalendarHeading,
    RangeCalendarNext,
    RangeCalendarPrev,
} from 'radix-vue'
import { computed } from 'vue'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { buttonVariants } from '@/components/ui/button'

const props = defineProps({
    modelValue: { type: Object, required: false },
    placeholder: { type: Object, required: false },
    weekdayFormat: { type: String, required: false, default: 'short' },
    numberOfMonths: { type: Number, required: false, default: 2 },
    minValue: { type: Object, required: false },
    maxValue: { type: Object, required: false },
    class: { type: String, required: false },
})

const emits = defineEmits(['update:modelValue', 'update:placeholder'])

const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props
    return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
    <RangeCalendarRoot
        v-slot="{ grid, weekDays }"
        :class="cn('p-3', props.class)"
        v-bind="forwarded"
    >
        <RangeCalendarHeader class="flex w-full items-center justify-between gap-2 pb-2">
            <RangeCalendarPrev
                :class="cn(
                    buttonVariants({ variant: 'outline' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 shrink-0'
                )"
            >
                <ChevronLeft class="h-4 w-4" />
            </RangeCalendarPrev>
            <RangeCalendarHeading class="text-sm font-medium grow text-center" />
            <RangeCalendarNext
                :class="cn(
                    buttonVariants({ variant: 'outline' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 shrink-0'
                )"
            >
                <ChevronRight class="h-4 w-4" />
            </RangeCalendarNext>
        </RangeCalendarHeader>

        <div class="flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
            <RangeCalendarGrid v-for="month in grid" :key="month.value.toString()" class="w-full border-collapse select-none space-y-1">
                <RangeCalendarGridHead>
                    <RangeCalendarGridRow class="flex">
                        <RangeCalendarHeadCell
                            v-for="day in weekDays"
                            :key="day"
                            class="w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground"
                        >
                            {{ day }}
                        </RangeCalendarHeadCell>
                    </RangeCalendarGridRow>
                </RangeCalendarGridHead>
                <RangeCalendarGridBody>
                    <RangeCalendarGridRow
                        v-for="(weekDates, index) in month.rows"
                        :key="`weekDate-${index}`"
                        class="mt-2 flex w-full"
                    >
                        <RangeCalendarCell
                            v-for="weekDate in weekDates"
                            :key="weekDate.toString()"
                            :date="weekDate"
                            class="relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([data-selected])]:bg-accent first:[&:has([data-selected])]:rounded-l-md last:[&:has([data-selected])]:rounded-r-md [&:has([data-selection-end])]:rounded-r-md [&:has([data-selection-start])]:rounded-l-md [&:has([data-selected][data-outside-view])]:bg-accent/50"
                        >
                            <RangeCalendarCellTrigger
                                :day="weekDate"
                                :month="month.value"
                                :class="cn(
                                    buttonVariants({ variant: 'ghost' }),
                                    'h-9 w-9 p-0 font-normal',
                                    '[&[data-today]:not([data-selected])]:bg-accent [&[data-today]:not([data-selected])]:text-accent-foreground',
                                    '[&[data-selection-start]]:bg-primary [&[data-selection-start]]:text-primary-foreground [&[data-selection-start]]:hover:bg-primary [&[data-selection-start]]:hover:text-primary-foreground [&[data-selection-start]]:focus:bg-primary [&[data-selection-start]]:focus:text-primary-foreground',
                                    '[&[data-selection-end]]:bg-primary [&[data-selection-end]]:text-primary-foreground [&[data-selection-end]]:hover:bg-primary [&[data-selection-end]]:hover:text-primary-foreground [&[data-selection-end]]:focus:bg-primary [&[data-selection-end]]:focus:text-primary-foreground',
                                    '[&[data-selected]:not([data-selection-start]):not([data-selection-end])]:bg-accent [&[data-selected]:not([data-selection-start]):not([data-selection-end])]:text-accent-foreground',
                                    '[&[data-outside-view]]:text-muted-foreground [&[data-outside-view]]:opacity-50 [&[data-outside-view][data-selected]]:bg-accent/50 [&[data-outside-view][data-selected]]:text-muted-foreground [&[data-outside-view][data-selected]]:opacity-30',
                                    '[&[data-unavailable]]:text-destructive-foreground [&[data-unavailable]]:line-through',
                                    '[&[data-disabled]]:text-muted-foreground [&[data-disabled]]:opacity-50',
                                )"
                            />
                        </RangeCalendarCell>
                    </RangeCalendarGridRow>
                </RangeCalendarGridBody>
            </RangeCalendarGrid>
        </div>
    </RangeCalendarRoot>
</template>

